<div class="student-registration-container">
  <!-- Header -->
  <div class="registration-header">
    <div class="header-content">
      <h1 class="page-title">{{ 'STUDENT_REGISTRATION.TITLE' | translate }}</h1>
      <p class="page-description">{{ 'STUDENT_REGISTRATION.DESCRIPTION' | translate }}</p>
    </div>
    
    <!-- Progress Steps -->
    <div class="progress-steps">
      <div 
        *ngFor="let step of stepsArray; let i = index" 
        class="step-item"
        [class.active]="step === currentStep"
        [class.completed]="step < currentStep"
      >
        <div class="step-number">{{ step }}</div>
        <div class="step-label">{{ getStepTitle(step) }}</div>
      </div>
    </div>
  </div>

  <!-- Main Form -->
  <div class="registration-form-container">
    <form [formGroup]="registrationForm" (ngSubmit)="onSubmit()" class="registration-form">
      
      <!-- Step 1: Personal Information -->
      <div *ngIf="currentStep === 1" class="form-step">
        <div class="step-header">
          <h2>{{ getStepTitle(1) }}</h2>
          <p>{{ getStepDescription(1) }}</p>
        </div>

        <div class="form-section">
          <h3>{{ 'STUDENT_REGISTRATION.SECTIONS.BASIC_INFO' | translate }}</h3>
          <div formGroupName="personalInfo" class="form-grid">
            <div class="form-field">
              <label for="first_name">{{ 'STUDENTS.FIRST_NAME' | translate }} *</label>
              <input 
                id="first_name"
                type="text" 
                pInputText 
                formControlName="first_name"
                [class.invalid]="isFieldInvalid('first_name', 'personalInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.FIRST_NAME' | translate"
              />
              <small class="error-message" *ngIf="isFieldInvalid('first_name', 'personalInfo')">
                {{ getValidationMessage('first_name', 'personalInfo') }}
              </small>
            </div>

            <div class="form-field">
              <label for="last_name">{{ 'STUDENTS.LAST_NAME' | translate }} *</label>
              <input 
                id="last_name"
                type="text" 
                pInputText 
                formControlName="last_name"
                [class.invalid]="isFieldInvalid('last_name', 'personalInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.LAST_NAME' | translate"
              />
              <small class="error-message" *ngIf="isFieldInvalid('last_name', 'personalInfo')">
                {{ getValidationMessage('last_name', 'personalInfo') }}
              </small>
            </div>

            <div class="form-field">
              <label for="email">{{ 'STUDENTS.EMAIL_ADDRESS' | translate }} *</label>
              <input 
                id="email"
                type="email" 
                pInputText 
                formControlName="email"
                [class.invalid]="isFieldInvalid('email', 'personalInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.EMAIL' | translate"
              />
              <small class="error-message" *ngIf="isFieldInvalid('email', 'personalInfo')">
                {{ getValidationMessage('email', 'personalInfo') }}
              </small>
            </div>

            <div class="form-field">
              <label for="phone_number">{{ 'STUDENTS.PHONE_NUMBER' | translate }} *</label>
              <input 
                id="phone_number"
                type="tel" 
                pInputText 
                formControlName="phone_number"
                [class.invalid]="isFieldInvalid('phone_number', 'personalInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.PHONE' | translate"
              />
              <small class="error-message" *ngIf="isFieldInvalid('phone_number', 'personalInfo')">
                {{ getValidationMessage('phone_number', 'personalInfo') }}
              </small>
            </div>

            <div class="form-field">
              <label for="date_of_birth">{{ 'STUDENTS.DATE_OF_BIRTH' | translate }} *</label>
              <p-calendar 
                id="date_of_birth"
                appendTo="body"
                formControlName="date_of_birth"
                [showIcon]="true"
                [showButtonBar]="true"
                [maxDate]="maxDate"
                dateFormat="dd/mm/yy"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.DATE_OF_BIRTH' | translate"
                [class.invalid]="isFieldInvalid('date_of_birth', 'personalInfo')"
              ></p-calendar>
              <small class="error-message" *ngIf="isFieldInvalid('date_of_birth', 'personalInfo')">
                {{ getValidationMessage('date_of_birth', 'personalInfo') }}
              </small>
            </div>

            <div class="form-field">
              <label for="gender">{{ 'STUDENTS.GENDER' | translate }} *</label>
              <p-dropdown appendTo="body" 
                id="gender"
                formControlName="gender"
                [options]="genderOptions"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.GENDER' | translate"
                [class.invalid]="isFieldInvalid('gender', 'personalInfo')"
                optionLabel="label"
                optionValue="value"
                class="capitalize"
              ></p-dropdown>
              <small class="error-message" *ngIf="isFieldInvalid('gender', 'personalInfo')">
                {{ getValidationMessage('gender', 'personalInfo') }}
              </small>
            </div>

            <div class="form-field full-width">
              <label for="address">{{ 'STUDENTS.ADDRESS' | translate }} *</label>
              <textarea 
                id="address"
                pInputTextarea 
                formControlName="address"
                [rows]="3"
                [class.invalid]="isFieldInvalid('address', 'personalInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.ADDRESS' | translate"
                class="capitalize"
              ></textarea>
              <small class="error-message" *ngIf="isFieldInvalid('address', 'personalInfo')">
                {{ getValidationMessage('address', 'personalInfo') }}
              </small>
            </div>

            <div class="form-field">
              <label for="place_of_birth">{{ 'STUDENT_REGISTRATION.PLACE_OF_BIRTH' | translate }}</label>
              <input 
                id="place_of_birth"
                type="text" 
                pInputText 
                formControlName="place_of_birth"
                [class.invalid]="isFieldInvalid('place_of_birth', 'personalInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.PLACE_OF_BIRTH' | translate"
              />
              <small class="error-message" *ngIf="isFieldInvalid('place_of_birth', 'personalInfo')">
                {{ getValidationMessage('place_of_birth', 'personalInfo') }}
              </small>
            </div>
          </div>
        </div>

        <!-- Profile Photo Upload -->
        <div class="form-section">
          <h3>{{ 'STUDENT_REGISTRATION.SECTIONS.PROFILE_PHOTO' | translate }} *</h3>
          <div class="photo-upload">
            <div class="upload-area" *ngIf="!profilePhoto">
              <p-fileUpload 
                mode="basic" 
                name="profile_photo"
                accept="image/*"
                maxFileSize="5000000"
                [chooseLabel]="'STUDENT_REGISTRATION.CHOOSE_PHOTO' | translate"
                (onSelect)="onProfilePhotoSelect($event)"
                [auto]="true"
                [showCancelButton]="false"
              ></p-fileUpload>
              <p class="upload-hint">{{ 'STUDENT_REGISTRATION.UPLOAD_HINT' | translate }}</p>
            </div>
            
            <div class="photo-preview" *ngIf="profilePhoto">
              <img [src]="getPhotoPreview()" [alt]="'STUDENT_REGISTRATION.PROFILE_PHOTO_PREVIEW' | translate" />
              <div class="photo-actions">
                <p-button 
                  icon="pi pi-pencil" 
                  severity="secondary"
                  (onClick)="removeProfilePhoto()"
                  [pTooltip]="'STUDENT_REGISTRATION.CHANGE_PHOTO' | translate"
                ></p-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 2: Family Information -->
      <div *ngIf="currentStep === 2" class="form-step">
        <div class="step-header">
          <h2>{{ getStepTitle(2) }}</h2>
          <p>{{ getStepDescription(2) }}</p>
        </div>

        <div class="form-section">
          <h3>{{ 'STUDENT_REGISTRATION.SECTIONS.FATHERS_INFO' | translate }}</h3>
          <div formGroupName="familyInfo" class="form-grid">
            <div class="form-field">
              <label for="fathers_name">{{ 'STUDENTS.PARENT_NAME' | translate }}</label>
              <input 
                id="fathers_name"
                type="text" 
                pInputText 
                formControlName="fathers_name"
                [class.invalid]="isFieldInvalid('fathers_name', 'familyInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.FATHERS_NAME' | translate"
              />
              <small class="error-message" *ngIf="isFieldInvalid('fathers_name', 'familyInfo')">
                {{ getValidationMessage('fathers_name', 'familyInfo') }}
              </small>
            </div>

            <div class="form-field">
              <label for="fathers_phone">{{ 'STUDENTS.PARENT_PHONE' | translate }}</label>
              <input 
                id="fathers_phone"
                type="tel" 
                pInputText 
                formControlName="fathers_phone"
                [class.invalid]="isFieldInvalid('fathers_phone', 'familyInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.FATHERS_PHONE' | translate"
              />
              <small class="error-message" *ngIf="isFieldInvalid('fathers_phone', 'familyInfo')">
                {{ getValidationMessage('fathers_phone', 'familyInfo') }}
              </small>
            </div>

            <div class="form-field">
              <label for="fathers_occupation">{{ 'STUDENT_REGISTRATION.FIELDS.FATHERS_OCCUPATION' | translate }}</label>
              <input 
                id="fathers_occupation"
                type="text" 
                pInputText 
                formControlName="fathers_occupation"
                [class.invalid]="isFieldInvalid('fathers_occupation', 'familyInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.FATHERS_OCCUPATION' | translate"
              />
              <small class="error-message" *ngIf="isFieldInvalid('fathers_occupation', 'familyInfo')">
                {{ getValidationMessage('fathers_occupation', 'familyInfo') }}
              </small>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3>{{ 'STUDENT_REGISTRATION.SECTIONS.MOTHERS_INFO' | translate }}</h3>
          <div formGroupName="familyInfo" class="form-grid">
            <div class="form-field">
              <label for="mothers_name">{{ 'STUDENT_REGISTRATION.FIELDS.MOTHERS_NAME' | translate }}</label>
              <input 
                id="mothers_name"
                type="text" 
                pInputText 
                formControlName="mothers_name"
                [class.invalid]="isFieldInvalid('mothers_name', 'familyInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.MOTHERS_NAME' | translate"
              />
              <small class="error-message" *ngIf="isFieldInvalid('mothers_name', 'familyInfo')">
                {{ getValidationMessage('mothers_name', 'familyInfo') }}
              </small>
            </div>

            <div class="form-field">
              <label for="mothers_phone">{{ 'STUDENT_REGISTRATION.FIELDS.MOTHERS_PHONE' | translate }}</label>
              <input 
                id="mothers_phone"
                type="tel" 
                pInputText 
                formControlName="mothers_phone"
                [class.invalid]="isFieldInvalid('mothers_phone', 'familyInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.MOTHERS_PHONE' | translate"
              />
              <small class="error-message" *ngIf="isFieldInvalid('mothers_phone', 'familyInfo')">
                {{ getValidationMessage('mothers_phone', 'familyInfo') }}
              </small>
            </div>

            <div class="form-field">
              <label for="mothers_occupation">{{ 'STUDENT_REGISTRATION.FIELDS.MOTHERS_OCCUPATION' | translate }}</label>
              <input 
                id="mothers_occupation"
                type="text" 
                pInputText 
                formControlName="mothers_occupation"
                [class.invalid]="isFieldInvalid('mothers_occupation', 'familyInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.MOTHERS_OCCUPATION' | translate"
              />
              <small class="error-message" *ngIf="isFieldInvalid('mothers_occupation', 'familyInfo')">
                {{ getValidationMessage('mothers_occupation', 'familyInfo') }}
              </small>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3>{{ 'STUDENT_REGISTRATION.SECTIONS.GUARDIAN_INFO' | translate }}</h3>
          <div formGroupName="familyInfo" class="form-grid">
            <div class="form-field">
              <label for="guardian_name">{{ 'STUDENT_REGISTRATION.FIELDS.GUARDIAN_NAME' | translate }}</label>
              <input 
                id="guardian_name"
                type="text" 
                pInputText 
                formControlName="guardian_name"
                [class.invalid]="isFieldInvalid('guardian_name', 'familyInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.GUARDIAN_NAME' | translate"
              />
              <small class="error-message" *ngIf="isFieldInvalid('guardian_name', 'familyInfo')">
                {{ getValidationMessage('guardian_name', 'familyInfo') }}
              </small>
            </div>

            <div class="form-field">
              <label for="guardian_contact_phone">{{ 'STUDENT_REGISTRATION.FIELDS.GUARDIAN_PHONE' | translate }}</label>
              <input 
                id="guardian_contact_phone"
                type="tel" 
                pInputText 
                formControlName="guardian_contact_phone"
                [class.invalid]="isFieldInvalid('guardian_contact_phone', 'familyInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.GUARDIAN_PHONE' | translate"
              />
              <small class="error-message" *ngIf="isFieldInvalid('guardian_contact_phone', 'familyInfo')">
                {{ getValidationMessage('guardian_contact_phone', 'familyInfo') }}
              </small>
            </div>

            <div class="form-field">
              <label for="guardian_relationship">{{ 'STUDENT_REGISTRATION.FIELDS.GUARDIAN_RELATIONSHIP' | translate }}</label>
              <input 
                id="guardian_relationship"
                type="text" 
                pInputText 
                formControlName="guardian_relationship"
                [class.invalid]="isFieldInvalid('guardian_relationship', 'familyInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.GUARDIAN_RELATIONSHIP' | translate"
              />
              <small class="error-message" *ngIf="isFieldInvalid('guardian_relationship', 'familyInfo')">
                {{ getValidationMessage('guardian_relationship', 'familyInfo') }}
              </small>
            </div>

            <div class="form-field">
              <label for="guardian_occupation">{{ 'STUDENT_REGISTRATION.FIELDS.GUARDIAN_OCCUPATION' | translate }}</label>
              <input 
                id="guardian_occupation"
                type="text" 
                pInputText 
                formControlName="guardian_occupation"
                [class.invalid]="isFieldInvalid('guardian_occupation', 'familyInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.GUARDIAN_OCCUPATION' | translate"
              />
              <small class="error-message" *ngIf="isFieldInvalid('guardian_occupation', 'familyInfo')">
                {{ getValidationMessage('guardian_occupation', 'familyInfo') }}
              </small>
            </div>

            <div class="form-field full-width">
              <label for="guardian_address">{{ 'STUDENT_REGISTRATION.FIELDS.GUARDIAN_ADDRESS' | translate }}</label>
              <textarea 
                id="guardian_address"
                pInputTextarea 
                formControlName="guardian_address"
                [rows]="3"
                [class.invalid]="isFieldInvalid('guardian_address', 'familyInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.GUARDIAN_ADDRESS' | translate"
              ></textarea>
              <small class="error-message" *ngIf="isFieldInvalid('guardian_address', 'familyInfo')">
                {{ getValidationMessage('guardian_address', 'familyInfo') }}
              </small>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 3: Academic Information -->
      <div *ngIf="currentStep === 3" class="form-step">
        <div class="step-header">
          <h2>{{ getStepTitle(3) }}</h2>
          <p>{{ getStepDescription(3) }}</p>
        </div>

        <div class="form-section">
          <h3>{{ 'STUDENT_REGISTRATION.SECTIONS.ACADEMIC_DETAILS' | translate }}</h3>
          <div formGroupName="academicInfo" class="form-grid">

            <div class="form-field">
              <label for="specialization">{{ 'STUDENT_REGISTRATION.FIELDS.FACULTY' | translate }} *</label>
              <p-dropdown appendTo="body" 
                id="specialization"
                formControlName="specialization"
                [options]="specializations"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.SELECT_SPECIALIZATION' | translate"
                [class.invalid]="isFieldInvalid('specialization', 'academicInfo')"
                optionLabel="name"
                optionValue="id"
                class="capitalize"
              ></p-dropdown>
            </div>

            <div class="form-field">
              <label for="department">{{ 'STUDENT_REGISTRATION.FIELDS.DEPARTMENT' | translate }} *</label>
              <p-dropdown appendTo="body" 
                id="department"
                formControlName="department"
                [options]="filteredDepartments"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.SELECT_DEPARTMENT' | translate"
                [class.invalid]="isFieldInvalid('department', 'academicInfo')"
                optionLabel="name"
                [disabled]="!filteredDepartments.length"
                optionValue="id"
                class="capitalize"
              ></p-dropdown>
            </div>

            <div class="form-field">
              <label for="specialty">{{ 'STUDENT_REGISTRATION.FIELDS.SPECIALTY' | translate }} *</label>
              <p-dropdown appendTo="body" 
                id="specialty"
                formControlName="series"
                [options]="filteredSeries"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.SELECT_SPECIALTY' | translate"
                [class.invalid]="isFieldInvalid('series', 'academicInfo')"
                optionLabel="name"
                optionValue="id"
                [disabled]="!filteredSeries.length"
                class="capitalize"
              ></p-dropdown>
              <small class="error-message" *ngIf="isFieldInvalid('series', 'academicInfo')">
                {{ getValidationMessage('series', 'academicInfo') }}
              </small>
            </div>
            
            <div class="form-field">
              <label for="academic_level">{{ 'STUDENT_REGISTRATION.FIELDS.ACADEMIC_LEVEL' | translate }} *</label>
              <p-dropdown appendTo="body" 
                id="academic_level"
                formControlName="academic_level"
                [options]="academicLevels"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.SELECT_ACADEMIC_LEVEL' | translate"
                [class.invalid]="isFieldInvalid('academic_level', 'academicInfo')"
                optionLabel="name"
                optionValue="id"
                class="capitalize"
              ></p-dropdown>
              <small class="error-message" *ngIf="isFieldInvalid('academic_level', 'academicInfo')">
                {{ getValidationMessage('academic_level', 'academicInfo') }}
              </small>
            </div>

            <!-- <div class="form-field">
              <label for="previous_school">Previous School</label>
              <input 
                id="previous_school"
                type="text" 
                pInputText 
                formControlName="previous_school"
                [class.invalid]="isFieldInvalid('previous_school', 'academicInfo')"
                [placeholder]="'COMMON.ENTER_PREVIOUS_SCHOOL' | translate"
              />
              <small class="error-message" *ngIf="isFieldInvalid('previous_school', 'academicInfo')">
                {{ getValidationMessage('previous_school', 'academicInfo') }}
              </small>
            </div>

            <div class="form-field">
              <label for="previous_grade">Previous Grade</label>
              <input 
                id="previous_grade"
                type="text" 
                pInputText 
                formControlName="previous_grade"
                [class.invalid]="isFieldInvalid('previous_grade', 'academicInfo')"
                [placeholder]="'COMMON.ENTER_PREVIOUS_GRADE' | translate"
              />
              <small class="error-message" *ngIf="isFieldInvalid('previous_grade', 'academicInfo')">
                {{ getValidationMessage('previous_grade', 'academicInfo') }}
              </small>
            </div>

            <div class="form-field full-width">
              <label for="transfer_reason">Transfer Reason</label>
              <textarea 
                id="transfer_reason"
                pInputTextarea 
                formControlName="transfer_reason"
                [rows]="3"
                [class.invalid]="isFieldInvalid('transfer_reason', 'academicInfo')"
                placeholder="Explain reason for transfer (if applicable)"
              ></textarea>
              <small class="error-message" *ngIf="isFieldInvalid('transfer_reason', 'academicInfo')">
                {{ getValidationMessage('transfer_reason', 'academicInfo') }}
              </small>
            </div> -->

          </div>
        </div>

        <div class="form-section">
          <h3>{{ 'STUDENT_REGISTRATION.SECTIONS.SPECIAL_REQUIREMENTS' | translate }}</h3>
          <div formGroupName="academicInfo" class="form-grid">
            <div class="form-field full-width">
              <label for="special_needs">{{ 'STUDENT_REGISTRATION.FIELDS.SPECIAL_NEEDS' | translate }}</label>
              <textarea 
                id="special_needs"
                pInputTextarea 
                formControlName="special_needs"
                [rows]="2"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.SPECIAL_NEEDS_DESC' | translate"
              ></textarea>
            </div>

            <div class="form-field full-width">
              <label for="medical_conditions">{{ 'STUDENT_REGISTRATION.FIELDS.MEDICAL_CONDITIONS' | translate }}</label>
              <textarea 
                id="medical_conditions"
                pInputTextarea 
                formControlName="medical_conditions"
                [rows]="2"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.MEDICAL_CONDITIONS_DESC' | translate"
              ></textarea>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3>{{ 'STUDENT_REGISTRATION.SECTIONS.EMERGENCY_CONTACT' | translate }}</h3>
          <div formGroupName="academicInfo" class="form-grid">
            <div class="form-field">
              <label for="emergency_contact">{{ 'STUDENT_REGISTRATION.FIELDS.EMERGENCY_CONTACT_PHONE' | translate }} *</label>
              <input 
                id="emergency_contact"
                type="tel" 
                pInputText 
                formControlName="emergency_contact"
                [class.invalid]="isFieldInvalid('emergency_contact', 'academicInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.EMERGENCY_CONTACT_PHONE' | translate"
              />
              <small class="error-message" *ngIf="isFieldInvalid('emergency_contact', 'academicInfo')">
                {{ getValidationMessage('emergency_contact', 'academicInfo') }}
              </small>
            </div>

            <div class="form-field">
              <label for="emergency_relationship">{{ 'STUDENT_REGISTRATION.FIELDS.EMERGENCY_RELATIONSHIP' | translate }} *</label>
              <input 
                id="emergency_relationship"
                type="text" 
                pInputText 
                formControlName="emergency_relationship"
                [class.invalid]="isFieldInvalid('emergency_relationship', 'academicInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.EMERGENCY_RELATIONSHIP' | translate"
              />
              <small class="error-message" *ngIf="isFieldInvalid('emergency_relationship', 'academicInfo')">
                {{ getValidationMessage('emergency_relationship', 'academicInfo') }}
              </small>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 4: Additional Information -->
      <div *ngIf="currentStep === 4" class="form-step">
        <div class="step-header">
          <h2>{{ getStepTitle(4) }}</h2>
          <p>{{ getStepDescription(4) }}</p>
        </div>

        <div class="form-section">
          <h3>{{ 'STUDENT_REGISTRATION.SECTIONS.STUDENT_DETAILS' | translate }}</h3>
          <div formGroupName="additionalInfo" class="form-grid">
            <div class="form-field">
              <label for="hobbies">{{ 'STUDENT_REGISTRATION.FIELDS.HOBBIES_INTERESTS' | translate }}</label>
              <textarea 
                id="hobbies"
                pInputTextarea 
                formControlName="hobbies"
                [rows]="2"
                [class.invalid]="isFieldInvalid('hobbies', 'additionalInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.HOBBIES_DESC' | translate"
              ></textarea>
              <small class="error-message" *ngIf="isFieldInvalid('hobbies', 'additionalInfo')">
                {{ getValidationMessage('hobbies', 'additionalInfo') }}
              </small>
            </div>

            <div class="form-field">
              <label for="languages">{{ 'STUDENT_REGISTRATION.FIELDS.LANGUAGES_SPOKEN' | translate }}</label>
              <input 
                id="languages"
                type="text" 
                pInputText 
                formControlName="languages"
                [class.invalid]="isFieldInvalid('languages', 'additionalInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.LANGUAGES_EXAMPLE' | translate"
              />
              <small class="error-message" *ngIf="isFieldInvalid('languages', 'additionalInfo')">
                {{ getValidationMessage('languages', 'additionalInfo') }}
              </small>
            </div>

            <div class="form-field full-width">
              <label for="achievements">{{ 'STUDENT_REGISTRATION.FIELDS.ACADEMIC_ACHIEVEMENTS' | translate }}</label>
              <textarea 
                id="achievements"
                pInputTextarea 
                formControlName="achievements"
                [rows]="2"
                [class.invalid]="isFieldInvalid('achievements', 'additionalInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.ACHIEVEMENTS_DESC' | translate"
              ></textarea>
              <small class="error-message" *ngIf="isFieldInvalid('achievements', 'additionalInfo')">
                {{ getValidationMessage('achievements', 'additionalInfo') }}
              </small>
            </div>

            <div class="form-field full-width">
              <label for="goals">{{ 'STUDENT_REGISTRATION.FIELDS.EDUCATIONAL_GOALS' | translate }}</label>
              <textarea 
                id="goals"
                pInputTextarea 
                formControlName="goals"
                [rows]="2"
                [class.invalid]="isFieldInvalid('goals', 'additionalInfo')"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.GOALS_DESC' | translate"
              ></textarea>
              <small class="error-message" *ngIf="isFieldInvalid('goals', 'additionalInfo')">
                {{ getValidationMessage('goals', 'additionalInfo') }}
              </small>
            </div>

            <div class="form-field full-width">
              <label for="notes">{{ 'STUDENT_REGISTRATION.FIELDS.ADDITIONAL_NOTES' | translate }}</label>
              <textarea 
                id="notes"
                pInputTextarea 
                formControlName="notes"
                [rows]="3"
                [placeholder]="'STUDENT_REGISTRATION.PLACEHOLDERS.ADDITIONAL_NOTES_DESC' | translate"
              ></textarea>
            </div>
          </div>
        </div>

        <!-- Document Upload -->
        <div class="form-section">
          <h3>{{ 'STUDENT_REGISTRATION.SECTIONS.SUPPORTING_DOCUMENTS' | translate }}</h3>
          <div class="document-upload">
            <p-fileUpload 
              mode="basic" 
              name="documents[]"
              accept=".pdf,.doc,.docx"
              maxFileSize="5000000"
              multiple="true"
              [chooseLabel]="'STUDENT_REGISTRATION.CHOOSE_DOCUMENTS' | translate"
              (onSelect)="onDocumentSelect($event)"
              [auto]="true"
              [showCancelButton]="false"
            ></p-fileUpload>
            <p class="upload-hint">{{ 'STUDENT_REGISTRATION.DOCUMENTS_UPLOAD_HINT' | translate }}</p>
            
            <div class="uploaded-documents" *ngIf="documents.length > 0">
              <h4>{{ 'STUDENT_REGISTRATION.UPLOADED_DOCUMENTS' | translate }}</h4>
              <div class="document-list">
                <div 
                  *ngFor="let doc of documents; let i = index" 
                  class="document-item"
                >
                  <i class="pi pi-file-pdf"></i>
                  <span class="document-name">{{ doc.name }}</span>
                  <p-button 
                    icon="pi pi-times" 
                    severity="danger"
                    size="small"
                    (onClick)="removeDocument(i)"
                    [pTooltip]="'STUDENT_REGISTRATION.REMOVE_DOCUMENT' | translate"
                  ></p-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Form Actions -->
      <div class="form-actions">
        <div class="action-buttons">
          <p-button 
            *ngIf="currentStep > 1"
            [label]="'STUDENT_REGISTRATION.FORM_ACTIONS.PREVIOUS' | translate" 
            icon="pi pi-arrow-left" 
            severity="secondary"
            (onClick)="previousStep()"
            [disabled]="isSubmitting"
          ></p-button>
          
          <p-button 
            *ngIf="currentStep < totalSteps"
            [label]="'STUDENT_REGISTRATION.FORM_ACTIONS.NEXT' | translate" 
            icon="pi pi-arrow-right" 
            iconPos="right"
            severity="primary"
            (onClick)="nextStep()"
            [disabled]="isSubmitting"
          ></p-button>
          
          <p-button 
            *ngIf="currentStep === totalSteps"
            [label]="'STUDENT_REGISTRATION.FORM_ACTIONS.SUBMIT_REGISTRATION' | translate" 
            icon="pi pi-check" 
            iconPos="right"
            severity="success"
            type="submit"
            [loading]="isSubmitting"
            [disabled]="isSubmitting"
          ></p-button>
        </div>
        
        <div class="form-progress">
          <span class="progress-text">{{ 'STUDENT_REGISTRATION.FORM_ACTIONS.STEP_PROGRESS' | translate: {current: currentStep, total: totalSteps} }}</span>
          <p-progressBar 
            [value]="(currentStep / totalSteps) * 100" 
            [showValue]="false"
            styleClass="registration-progress"
          ></p-progressBar>
        </div>
      </div>
    </form>
  </div>

  <!-- Success Message -->
  <p-toast></p-toast>
</div>

